import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { equipment as equipmentApi } from '@/utils/api'
import { useEquipmentMeta, useInvalidateEquipment } from '@/hooks/useEquipment'
import { createEquipmentSchema, type CreateEquipmentInput } from '@/schemas/equipment'
import type { Equipment } from '@/types/equipment'

const selectCls = 'flex h-9 w-full rounded-md border border-input bg-white px-3 py-1 text-sm'

interface Props {
  open: boolean
  onClose: () => void
  onCreated: (item: Equipment) => void
}

export default function CreateEquipmentDialog({ open, onClose, onCreated }: Props) {
  const { data: meta } = useEquipmentMeta()
  const invalidate = useInvalidateEquipment()
  const { data: nextCode } = useQuery({
    queryKey: ['equipment', 'next-code', open],
    queryFn: () => equipmentApi.getNextCode().then((r) => r.data.nextCode as string),
    enabled: open,
  })

  const form = useForm<CreateEquipmentInput>({
    resolver: zodResolver(createEquipmentSchema),
    defaultValues: {
      name: '', category: '', manufacturer: '', model: '', serialNumber: '', purchaseDate: '', purchasePrice: '',
      warrantyUntil: '', supplier: '', invoiceNumber: '', dguvIntervalMonths: 12, dguvNextCheck: '', notes: '',
    },
  })

  const submit = async (data: CreateEquipmentInput) => {
    try {
      const res = await equipmentApi.create(data)
      toast.success(`Gerät ${res.data.unique_code} angelegt`)
      invalidate()
      form.reset()
      onCreated(res.data)
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.message || 'Anlegen fehlgeschlagen')
    }
  }

  const text = (name: keyof CreateEquipmentInput, label: string, props: Record<string, unknown> = {}) => (
    <FormField control={form.control} name={name} render={({ field }) => (
      <FormItem>
        <FormLabel>{label}</FormLabel>
        <FormControl><Input {...field} value={(field.value as string | number | undefined) ?? ''} {...props} /></FormControl>
        <FormMessage />
      </FormItem>
    )} />
  )

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader><DialogTitle>Neues Gerät</DialogTitle></DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(submit)} className="space-y-4">
            <div className="flex items-center gap-3 rounded-lg border bg-muted/50 p-3">
              <span className="text-sm text-muted-foreground">Gerätenummer:</span>
              <span className="font-mono text-lg font-bold text-primary-600">{nextCode ?? '…'}</span>
              <span className="text-xs text-muted-foreground">(automatisch, steht später auf dem Etikett)</span>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              {text('name', 'Name *', { placeholder: 'z.B. Hilti TE 30 Bohrhammer' })}
              <FormField control={form.control} name="category" render={({ field }) => (
                <FormItem>
                  <FormLabel>Kategorie *</FormLabel>
                  <FormControl>
                    <select {...field} className={selectCls}>
                      <option value="">Auswählen…</option>
                      {meta?.categories.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
                    </select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              {text('manufacturer', 'Hersteller', { placeholder: 'z.B. Hilti' })}
              {text('model', 'Modell', { placeholder: 'z.B. TE 30-A36' })}
              {text('serialNumber', 'Seriennummer')}
            </div>

            <div className="rounded-lg border p-3">
              <p className="mb-3 text-sm font-medium">Kauf & Gewährleistung</p>
              <div className="grid gap-4 sm:grid-cols-2">
                {text('purchaseDate', 'Kaufdatum', { type: 'date' })}
                {text('purchasePrice', 'Kaufpreis (€)', { placeholder: '349,90', inputMode: 'decimal' })}
                {text('supplier', 'Händler', { placeholder: 'z.B. Würth' })}
                {text('invoiceNumber', 'Rechnungsnr.')}
                {text('warrantyUntil', 'Garantie bis', { type: 'date' })}
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                Ohne „Garantie bis" rechnet das System mit 24 Monaten gesetzlicher Gewährleistung ab Kaufdatum.
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <FormField control={form.control} name="dguvIntervalMonths" render={({ field }) => (
                <FormItem>
                  <FormLabel>DGUV-Intervall</FormLabel>
                  <FormControl>
                    <select {...field} value={field.value ?? 12} onChange={(e) => field.onChange(parseInt(e.target.value, 10))} className={selectCls}>
                      <option value={0}>keine Prüfung</option>
                      <option value={6}>6 Monate</option>
                      <option value={12}>12 Monate</option>
                      <option value={24}>24 Monate</option>
                    </select>
                  </FormControl>
                </FormItem>
              )} />
              {text('dguvNextCheck', 'Nächste DGUV-Prüfung', { type: 'date' })}
            </div>

            <FormField control={form.control} name="notes" render={({ field }) => (
              <FormItem>
                <FormLabel>Notizen</FormLabel>
                <FormControl><textarea className="flex min-h-[60px] w-full rounded-md border border-input bg-white px-3 py-2 text-sm" {...field} value={field.value ?? ''} /></FormControl>
              </FormItem>
            )} />

            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" type="button" onClick={onClose}>Abbrechen</Button>
              <Button type="submit" disabled={form.formState.isSubmitting}>
                {form.formState.isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />} Gerät anlegen
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
